import styled, { css } from 'styled-components'

import * as breakpoint from '../../constants/breakpoints'
import * as color from '../../constants/colors'
import * as sizes from '../../constants/sizes'
import * as zindex from '../../constants/zindex'

import * as button from '../button'
import * as copy from '../copy'
import * as dropdown from '../dropdown'
import * as icons from '../../svg/icons'
import * as option from './option'

export type Status = 'focused' | 'error' | 'default'

export const Label = styled(copy.Text1)`
    color: ${color.GRAY_3};
    margin-right: 20px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: flex;
    margin-top: -1px;
    margin-bottom: 1px;
`

export const BaseButton = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 19.5px 15px;
    border: none;
    user-select: none;
    background: ${color.SAND_1};
    box-sizing: border-box;
    width: 100%;
    border-radius: 12px;
    cursor: pointer;

    &:focus {
        outline: none;
    }

    &:hover {
        background: ${color.SAND_2};
    }

    &:disabled {
        opacity: 0.5;
    }

    @media screen and (${breakpoint.TABLET}) {
        padding: 14.5px 15px;
    }
`

export const Button = styled(BaseButton)<{ status?: Status; borderless?: boolean }>`
    ${({ status }) => {
        if (status === 'focused') {
            return css`
                background: ${color.SAND_2};
            `
        }
    }}

    ${({ borderless }) => {
        if (borderless) {
            return css`
                background: transparent;
            `
        }
    }}
`

const BaseDrawer = styled.div`
    height: 0;
`

export const Drawer = styled(BaseDrawer)<{ isOpen: boolean }>`
    ${({ isOpen }) => {
        if (!isOpen) {
            return css`
                visibility: hidden;
            `
        }
    }};
`

export const BaseScrollList = styled.ul`
    position: relative; /* make this the offsetParent of all children (used for scroll) */
    list-style: none;
    padding: 0;
    margin: 0;
    background-color: ${color.SAND_1};
    overflow: hidden;
    z-index: ${zindex.ABOVE};
`

export const ScrollList = styled(BaseScrollList)<{ height?: number }>`
    ${({ height }) => {
        if (height !== undefined) {
            return css`
                max-height: ${height}px;
                overflow-y: auto;
            `
        }
    }}
`

export const CaretDown = styled(icons.ChevronDown).attrs({ size: 20 })`
    color: ${color.GRAY_3};
`

export const SearchBox = styled.div`
    padding: 10px;
`

export const BaseWrapper = styled.div`
    border: 1px solid ${color.GRAY_3};
    border-radius: 12px;

    ${ScrollList} {
        border: 1px solid ${color.GRAY_3};
        border-top: none;
        margin: 0 -1px 0 -1px;
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
    }
`

export type WrapperProps = {
    borderless?: boolean
    isOpen: boolean
    alignOptions: 'left' | 'right'
}

export const Wrapper = styled(BaseWrapper)<WrapperProps>`
    ${({ isOpen, borderless }) => {
        if (isOpen) {
            return css`
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
                /* specificity hack */

                ${Button} {
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                }

                ${Label} {
                    font-weight: 600;
                }
            `
        }

        if (borderless && !isOpen) {
            return css`
                border-color: transparent;
            `
        }
    }};

    ${({ alignOptions }) => {
        if (alignOptions === 'right') {
            return css`
                ${option.Wrapper} {
                    justify-content: end;
                }
            `
        }
    }}
`

export const DrawerHead = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;

    ${button.UnstyledButton} {
        padding: 30px;
    }
`

export const ResponsiveDrawer = styled(Drawer)`
    ${DrawerHead} {
        ${({ isOpen }) => {
            if (!isOpen) {
                return css`
                    display: none;
                `
            }
        }}
    }

    @media screen and (${breakpoint.TABLET}) {
        ${dropdown.Dropdown} {
            background-color: transparent;
        }

        ${DrawerHead} {
            display: none;
        }
    }

    @media not screen and (${breakpoint.TABLET}) {
        ${({ isOpen }) => {
            if (isOpen) {
                return css`
                    position: fixed;
                    top: ${sizes.HEADER_MOBILE_INTERNAL_HEIGHT};
                    height: calc(100% - ${sizes.HEADER_MOBILE_INTERNAL_HEIGHT});
                    left: 0;
                    right: 0;
                `
            }
        }}
        background-color: ${color.SAND_1};
        box-shadow: 0 10px 24px -24px ${color.GRAY_3} inset;

        ${dropdown.Dropdown} {
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            top: 78px;
        }

        ${ScrollList} {
            border: none;
            border-radius: 0;
            max-height: none;
            height: 100%;
        }

        ${option.Wrapper} {
            padding-left: 20px;
            padding-right: 20px;
        }
    }
`
